// Generated by tutorials/record.cjs from tutorials/articles/h65-allergy-protection-check.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h65 --text-only` (wording) or
// `node tutorials/record.cjs h65` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'allergy-protection-check',
  catalogId: 'h65',
  title: 'Checking allergy protection',
  summary: 'Check allergy protection makes sure every food allergy really keeps the food off the tray, warns in Selective Touch and blocks it in self-order, and helps you fix what does not.',
  audience: 'Administrators and dietary managers setting up a facility that is new to Dietech Online',
  area: 'setup',
  learn: [
    'Open the check and read the result at the top',
    'What each of the seven parts checks',
    'Fix an allergy that is not protected, from the page',
    'Name who approved each exception',
    'Block allergen items in self-order',
    'Use Proof to see what today\'s menu would leave off the tray',
    'When to run the check again',
  ],
  keywords: [
    'allergy',
    'allergies',
    'food allergy',
    'food allergies',
    'allergy protection',
    'check allergy protection',
    'allergies and food safety',
    'never serve',
    'never-serve',
    'allergy group',
    'allergen',
    'allergens',
    'exception',
    'exceptions',
    'self-order',
    'block allergen items',
    'proof',
    'go-live',
    'not protected',
    'getting started',
  ],
  steps: [
    {
      id: 'open',
      part: 'Open the check',
      heading: 'Open Check allergy protection',
      text: 'Open Food Data, Tables, Food Allergies. Select More actions (the three dots at the top right), then Check allergy protection.\n\nWhile your facility is being set up, the Setup checklist on your dashboard has the same link.',
      media: {
        image: {
          src: '/help/allergy-protection-check/open.webp',
          width: 1230,
          height: 582,
        },
        clip: {
          src: '/help/allergy-protection-check/open.mp4',
          width: 1230,
          height: 582,
          durationSec: 3.8,
        },
        alt: 'The Food Allergies table with the More actions menu open and Check allergy protection in it.',
      },
    },
    {
      id: 'verdict',
      part: 'Open the check',
      heading: 'The result at the top',
      text: 'The line at the top says how many blocking problems there are. A blocking problem means a resident could be served a food they are allergic to, and go-live cannot be signed off until it is fixed.\n\nBelow it, the seven parts of the check, each with a green check, a To look at mark or a red Blocking mark and a one-line summary. Select one to jump to it. Check again runs the whole check again.',
      media: {
        image: {
          src: '/help/allergy-protection-check/verdict.webp',
          width: 1280,
          height: 644,
        },
        alt: 'Check allergy protection: 2 blocking problems, Check again, and the seven parts: 1. Allergy list, 2. Protection (1 allergy is not protected), 3. Item coverage, 4. Exceptions, 5. EHR allergies, 6. Where it applies and 7. Proof.',
      },
    },
    {
      id: 'list',
      part: 'The allergy list and protection',
      heading: '1. Allergy list',
      text: 'How many food allergies you have, and any names that are the same once capitals and spaces are ignored. Keep one of each, and use Replace in Food Allergies to move residents onto it.\n\nIt also counts allergies no current resident has. That is fine for a new facility; they still need protecting before anyone is given one.',
      media: {
        image: {
          src: '/help/allergy-protection-check/list.webp',
          width: 1280,
          height: 260,
        },
        alt: '1. Allergy list, To look at: 10 food allergies on file, the duplicate names Tree Nuts and TREE NUTS, and 2 allergies not held by any current resident.',
      },
    },
    {
      id: 'protection',
      part: 'The allergy list and protection',
      heading: '2. Protection',
      text: 'An allergy protects a resident only when it is linked to an active never-serve adjustment on an allergy group, at every meal, and the resident has that adjustment on the meal pattern.\n\nThe allergies that need you come first, with what is wrong and a button to fix it. Not protected is blocking. Protected, check is worth a look, such as an allergy group no item carries yet. Show the other allergies lists the rest, including Record only: an allergy group marked as not a true allergy.',
      media: {
        image: {
          src: '/help/allergy-protection-check/protection.webp',
          width: 1280,
          height: 364,
        },
        alt: '2. Protection, Blocking: Tree Nuts Not protected, linked to Nothing, Not linked to a never-serve adjustment, with Link to Never serve tree nuts; and Soy Protected, check, no items carry the Soy allergy group yet.',
      },
    },
    {
      id: 'link',
      part: 'The allergy list and protection',
      heading: 'Link an allergy to its adjustment',
      text: 'Tree Nuts is not linked to anything, so it removes nothing and warns nobody. Select Link to Never serve tree nuts.\n\nThe link is saved. The resident who already has Tree Nuts still needs the adjustment on the meal pattern, so the row now says so, with Add it for 1 resident.',
      media: {
        image: {
          src: '/help/allergy-protection-check/link.webp',
          width: 1280,
          height: 454,
        },
        clip: {
          src: '/help/allergy-protection-check/link.mp4',
          width: 1280,
          height: 454,
          durationSec: 2.8,
        },
        alt: 'Saved: Tree Nuts is now linked (Never serve tree nuts), and the Tree Nuts row now says 1 resident does not have its never-serve adjustment on the meal pattern, with Add it for 1 resident.',
      },
    },
    {
      id: 'add-residents',
      part: 'The allergy list and protection',
      heading: 'Add it for the residents who have the allergy',
      text: 'Select Add it for 1 resident. The question says how many current residents have the allergy without its adjustment. Select Add for 1 resident: the adjustment is added to each meal pattern, just as adding the allergy on the resident page does, and the adjustment log records it.',
      media: {
        image: {
          src: '/help/allergy-protection-check/add-residents.webp',
          width: 944,
          height: 368,
        },
        clip: {
          src: '/help/allergy-protection-check/add-residents.mp4',
          width: 944,
          height: 368,
          durationSec: 2,
        },
        alt: 'The question Add the Tree Nuts adjustment: 1 current resident has Tree Nuts without its never-serve adjustment, with Cancel and Add for 1 resident.',
      },
    },
    {
      id: 'protected',
      part: 'The allergy list and protection',
      heading: 'Protected',
      text: 'When it is saved, the message says how many residents now have the adjustment, and Tree Nuts moves down to the protected allergies.',
      media: {
        image: {
          src: '/help/allergy-protection-check/protected.webp',
          width: 1280,
          height: 300,
        },
        clip: {
          src: '/help/allergy-protection-check/protected.mp4',
          width: 1280,
          height: 300,
          durationSec: 2.6,
        },
        alt: 'Saved: the Tree Nuts adjustment is now on the meal pattern of 1 resident, and only Soy is left in the list, as Protected, check.',
      },
    },
    {
      id: 'items',
      part: 'Items, exceptions and EHR allergies',
      heading: '3. Item coverage',
      text: 'PSI\'s items and recipes come with their allergy groups. Items your facility made itself carry only what someone added, so an allergen in one of them is invisible to every check.\n\nThis part lists your own items with no allergy group, those on today\'s menu first. Open each one and add its allergy groups, or confirm it has none.',
      media: {
        image: {
          src: '/help/allergy-protection-check/items.webp',
          width: 1280,
          height: 278,
        },
        alt: '3. Item coverage, To look at: 3 facility-made items have no allergy group, 2 on Fall/Winter Cycle 2026: House Granola and Chef Special Stir Fry, both On today\'s menu, and Garden Pasta Salad.',
      },
    },
    {
      id: 'exceptions',
      part: 'Items, exceptions and EHR allergies',
      heading: '4. Exceptions',
      text: 'An exception keeps an item on the tray even though it carries the allergen, such as a peanut-free cookie. Each one should have a named person who approved it.\n\nType the name under Approved by for any marked Nobody named, then select Save names. Exceptions on residents\' own adjustments are counted below; review them on each resident\'s Meal Pattern tab.',
      media: {
        image: {
          src: '/help/allergy-protection-check/exceptions.webp',
          width: 1280,
          height: 314,
        },
        clip: {
          src: '/help/allergy-protection-check/exceptions.mp4',
          width: 1280,
          height: 314,
          durationSec: 5.5,
        },
        alt: '4. Exceptions: Peanuts, Never serve peanuts, still serves the Peanut-Free Chocolate Chip Cookie, approved by Dana Morgan; Milk, Never serve milk, still serves the Lactose-Free Vanilla Pudding, now approved by Ruth Okafor, typed under Approved by and saved with Save names.',
      },
    },
    {
      id: 'ehr',
      part: 'Items, exceptions and EHR allergies',
      heading: '5. EHR allergies',
      text: 'If your EHR sends allergies, this part counts how many are mapped to your allergies. It lists any allergy text that matches none of yours, with the messages waiting for it, and any that map to an allergy that is not protected.\n\nSelect Open EMR/EHR to map them.',
      media: {
        image: {
          src: '/help/allergy-protection-check/ehr.webp',
          width: 1280,
          height: 214,
        },
        alt: '5. EHR allergies, To look at: 7 EHR allergies mapped, and NUTS-TREE sent by the EHR, matching none of yours, with 2 messages waiting until it is mapped.',
      },
    },
    {
      id: 'where',
      part: 'Where protection applies',
      heading: '6. Where it applies',
      text: 'Where the allergy acts: tray tickets always take the food off; recipe ingredients says whether tray tickets also look inside recipes; Selective Touch asks the staff member before adding an allergen item.\n\nIn self-order and bedside ordering, a resident who is only warned can still pick an allergen item. Select Block allergen items so they cannot.',
      media: {
        image: {
          src: '/help/allergy-protection-check/where.webp',
          width: 1280,
          height: 366,
        },
        alt: '6. Where it applies: Tray tickets, Removes the food; Recipe ingredients, On; Selective Touch, Warns; and Self-order and bedside, Warns only, with Block allergen items.',
      },
    },
    {
      id: 'no-blockers',
      part: 'Where protection applies',
      heading: 'No blocking problems',
      text: 'Self-order and bedside now block allergen items. Back at the top, the check says No blocking problems: every allergy removes or blocks the food where it should.\n\nIn Getting Started, Allergies and food safety can now be marked done.',
      media: {
        image: {
          src: '/help/allergy-protection-check/no-blockers.webp',
          width: 1280,
          height: 626,
        },
        clip: {
          src: '/help/allergy-protection-check/no-blockers.mp4',
          width: 1280,
          height: 626,
          durationSec: 4.8,
        },
        alt: 'The top of the check: No blocking problems, every allergy removes or blocks the food where it should, with the parts still To look at marked in the list.',
      },
    },
    {
      id: 'proof',
      part: 'Proof, and when to check again',
      heading: '7. Proof',
      text: 'Pick an allergy under Allergy. Proof lists what a resident with only that allergy would not be served from today\'s menu, meal by meal, and what an exception keeps on the tray. Items marked in a recipe ingredient are left off because of what is inside them.\n\nNo resident is read or changed.',
      media: {
        image: {
          src: '/help/allergy-protection-check/proof.webp',
          width: 1280,
          height: 444,
        },
        clip: {
          src: '/help/allergy-protection-check/proof.mp4',
          width: 1280,
          height: 444,
          durationSec: 4.9,
        },
        alt: '7. Proof with Peanuts picked: Fall/Winter Cycle 2026, today, 3 items are left off the tray for Peanuts: Peanut Butter Toast at breakfast, Thai Peanut Noodles at lunch (in a recipe ingredient) and Peanut Butter Cookie at dinner; the Peanut-Free Chocolate Chip Cookie is kept by an exception.',
      },
    },
    {
      id: 'again',
      part: 'Proof, and when to check again',
      heading: 'When to check again',
      text: 'Select Check again, at the top, after anything that changes how an allergy is protected: a new food allergy or allergy group, a change to a never-serve adjustment or its exceptions, new items or recipes your facility makes, a Data Update, or a change to the self-order settings.\n\nAnd always once more just before go-live: the Go-live check waits until there are no blocking problems.',
      textOnly: true,
    },
  ],
  tips: [
    'Fix the blocking problems first: they are the ones that could let a resident be served a food they are allergic to. The parts marked To look at are worth a look, but do not stop setup.',
    'Each heading in the list at the top jumps to its part of the page.',
    'Proof never reads or changes a resident. It is a safe way to show a dietitian or a surveyor what an allergy does.',
  ],
  notes: [
    'Check allergy protection is offered while your facility is being set up with Getting Started. It is the same check as the Allergies and food safety step, and go-live waits until it has no blocking problems.',
    'Looking at the check needs the Food Allergies table on your role. The one-click fixes need a higher Food Allergies level (adding an adjustment to residents also needs the Residents level), and Block allergen items needs the Facility settings level. Ask your administrator.',
    'Residents appear only as counts on this page, never by name.',
  ],
  related: [
    'allergies-likes-dislikes',
    'getting-started',
    'adjustments',
    'meal-patterns',
    'map-ehr-text',
    'tray-tickets',
  ],
  recorded: '2026-11-03',
};

export default article;
